import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { customerService } from '../../../services/customerService';
import CustomerFormModal from './CustomerFormModal';

interface QuoteHeaderSectionProps {
  header: {
    referenceCode?: string;
    customerName?: string;
    projectName?: string;
    status?: string;
    quoteDate?: string;
    validUntil?: string;
  };
  selectedCustomerId?: string;
  onUpdate: (header: any) => void;
  onCustomerSelect: (customerId: string, customerName: string) => void;
  onSave: () => void;
  onPreview: () => void;
  isSaving: boolean;
}

export default function QuoteHeaderSection({ 
  header, 
  selectedCustomerId,
  onUpdate,
  onCustomerSelect,
  onSave,
  onPreview,
  isSaving 
}: QuoteHeaderSectionProps) {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customerService.getAllCustomers();
      console.log('Loaded customers:', data);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading customers:', err);
      setError(err.message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerCreated = async (newCustomer: any) => {
    // Refresh customer list
    await loadCustomers();
    // Select the newly created customer
    onCustomerSelect(newCustomer.id, newCustomer.name);
    setShowCustomerModal(false);
  };

  const handleCustomerChange = (customerId: string) => {
    const selectedCustomer = customers.find(c => c.id === customerId);
    if (selectedCustomer) {
      onCustomerSelect(customerId, selectedCustomer.name);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Quote Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Reference Code - Auto-generated */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reference Code
          </label>
          <input
            type="text"
            value={header?.referenceCode || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-generated on save</p>
        </div>

        {/* Quote Date - Auto-generated */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quote Date
          </label>
          <input
            type="date"
            value={header?.quoteDate || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-set to today on save</p>
        </div>

        {/* Expiry Date - Auto-generated */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiry Date
          </label>
          <input
            type="date"
            value={header?.validUntil || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-set to 30 days from quote date</p>
        </div>

        {/* Customer Selector with Create Button */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer *
          </label>
          <div className="flex gap-2">
            <select
              key={customers.length} // Force re-render when customers change
              value={selectedCustomerId || ''}
              onChange={(e) => handleCustomerChange(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7CC363] focus:border-[#7CC363]"
              disabled={loading}
            >
              <option value="">
                {loading ? 'Loading customers...' : 'Select a customer'}
              </option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowCustomerModal(true)}
              className="px-3 py-2 bg-[#7CC363] text-white rounded-md hover:bg-[#6ab052] transition-colors flex items-center justify-center"
              title="Create new customer"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          )}
        </div>

        {/* Project Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Project Name
          </label>
          <input
            type="text"
            value={header?.projectName || ''}
            onChange={(e) => onUpdate({ ...header, projectName: e.target.value })}
            placeholder="Enter project name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7CC363] focus:border-[#7CC363]"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={header?.status || 'DRAFT'}
            onChange={(e) => onUpdate({ ...header, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7CC363] focus:border-[#7CC363]"
          >
            <option value="DRAFT">Draft</option>
            <option value="OPEN">Open</option>
            <option value="APPROVED">Approved</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Customer Form Modal */}
      {showCustomerModal && (
        <CustomerFormModal
          onClose={() => setShowCustomerModal(false)}
          onCustomerCreated={handleCustomerCreated}
        />
      )}
    </div>
  );
}